import {
    TTrackPageEvent,
    TTrackCartEvent,
    TTrackAddToCartEvent,
    TTrackRemoveFromCartEvent,
    TTrackUser,
    TTrackSearchEvent,
    TTrackImpression,
    TTrackClick,
} from '@com/core/common/types/tracking';

import { TCart, TCartItem } from '@com/core/common/types/cart';

import { setCookie, getCookie } from '@com/core/common/utils/client';

import uuid from 'uuid/v4'; // DO NOT CHANGE. Check with @Nipun, @Gihan or Watchdog SVC for used version

import Locale from '@com/core/common/utils/locale';

import { catalogSVC as Product } from '@com/core/common/services/api';
import TagManager from 'react-gtm-module';
import { reportEvent } from './sentry';

import { TMarketplace } from '../types/config';
import { TIngestParams } from '../types/recommendation';
import { TRecommendationsResponse } from '../types/catalog';
import { getMarketplaceConfig } from './config';

// eslint-disable-next-line no-underscore-dangle
const marketplace = process.env._MARKETPLACE as TMarketplace;
const marketplaceConfig = getMarketplaceConfig(marketplace);

export default class Analytics {
    static visitorId: string | null;

    /**
     * Get Visitor ID, save in cookie
     * @function getVisitorId
     * @returns  {string}
     */
    static getVisitorId = (): string => {
        if (typeof window === 'undefined') {
            reportEvent('Analytics initialized on SSR', 'Analytics.getVisitorId', 'warning');
            return '';
        }

        let visitorId = '';
        try {
            visitorId = getCookie('visitor_id') || '';
        } catch (e) {
            return '';
        }
        if (visitorId !== '') {
            return visitorId;
        }
        visitorId = uuid();
        try {
            setCookie('visitor_id', visitorId);
            return visitorId;
        } catch (e) {
            reportEvent('Error while setting userId', 'Analytics.getVisitorId', 'error');
        }
        return visitorId;
    };

    /**
     * Initializes Analytics
     * @function initialize
     * @returns {void}
     */
    public static initialize(): void {
        if (!this.visitorId) {
            this.visitorId = Analytics.getVisitorId();

            TagManager.initialize({
                gtmId: marketplaceConfig.gtm.gtmID,
                dataLayer: {
                    event: 'initUrl',
                    locale: Locale.getISOLocale(),
                    visitorId: Analytics.visitorId,
                    initUrl: window.location.href,
                    googleAnalyticsId: marketplaceConfig.gtm.gaID,
                },
            });
        }
    }

    /**
     * Emits a trackPage event to GTM
     * @function trackPage
     * @param {TTrackPageEvent} - Track page event details
     * @returns {void}
     */
    public static trackPage = (params: TTrackPageEvent): void => {
        try {
            if (!Analytics.visitorId) {
                Analytics.initialize();
            }

            TagManager.dataLayer({
                dataLayer: {
                    event: `page_${params.page}`,
                    eventAction: params.eventAction,
                    eventLabel: params.eventLabel,
                    value: params.value,
                    locale: Locale.getISOLocale(),
                    platform: 'web',
                    currency: Locale.getCurrencyCodeEN(),
                    uid: Analytics.visitorId,
                },
            });
        } catch (e) {
            reportEvent('Error during track page event', 'Analytics.trackPage', 'error', { error: e, params });
        }
    };

    /**
     * Emits a trackCart event to GTM and Criteof
     * @function trackCart
     * @param {TTrackCartEvent} - Track cart event details
     * @returns {void}
     */
    public static trackCart = (params: TTrackCartEvent): void => {
        try {
            if (!Analytics.visitorId) {
                Analytics.initialize();
            }

            TagManager.dataLayer({
                dataLayer: {
                    event: 'cart',
                    eventAction: params.eventAction,
                    eventLabel: params.eventLabel,
                    value: params.value,
                    locale: Locale.getSEOLocale(),
                    platform: params.platform,
                    currency: Locale.getCurrencyCodeEN(),
                    uid: Analytics.visitorId,
                    criteoBasketProducts: params.criteoBasketProducts,
                },
            });
        } catch (e) {
            reportEvent('Error during track cart event', 'Analytics.trackCart', 'error', e);
        }
    };

    /**
     * Emits a trackAddToCart event to GTM
     * @function trackAddToCart
     * @param {TTrackAddToCartEvent} - Track add to cart event details
     * @returns {void}
     */
    public static trackAddToCart = (params: TTrackAddToCartEvent): void => {
        try {
            if (!Analytics.visitorId) {
                Analytics.initialize();
            }

            TagManager.dataLayer({
                dataLayer: {
                    event: 'add_to_cart',
                    eventAction: params.eventAction,
                    eventLabel: params.eventLabel,
                    value: params.value,
                    locale: Locale.getSEOLocale(),
                    platform: params.platform,
                    currency: Locale.getCurrencyCodeEN(),
                    uid: Analytics.visitorId,
                },
            });
        } catch (e) {
            reportEvent('Error during track add to cart event', 'Analytics.trackAddToCart', 'error', e);
        }
    };

    /**
     * Emits a trackRemoveFromCart event to GTM
     * @function trackRemoveFromCart
     * @param {TTrackRemoveFromCartEvent} - Track remove from cart event details
     * @returns {void}
     */
    public static trackRemoveFromCart = (params: TTrackRemoveFromCartEvent): void => {
        try {
            if (!Analytics.visitorId) {
                Analytics.initialize();
            }
            TagManager.dataLayer({
                dataLayer: {
                    event: 'remove_from_cart',
                    eventAction: params.eventAction,
                    eventLabel: params.eventLabel,
                    value: params.value,
                    locale: Locale.getSEOLocale(),
                    platform: params.platform,
                    currency: Locale.getCurrencyCodeEN(),
                    uid: Analytics.visitorId,
                },
            });
        } catch (e) {
            reportEvent('Error during track remove from cart event', 'Analytics.trackRemoveFromCart', 'error', e);
        }
    };

    /**
     * Emits a trackUser event to GTM
     * @function trackPage
     * @param {TTrackUser} - Track user event details
     * @returns {void}
     */
    public static trackUser = (params: TTrackUser): void => {
        try {
            if (!Analytics.visitorId) {
                Analytics.initialize();
            }
            TagManager.dataLayer({
                dataLayer: {
                    event: params.event,
                    locale: Locale.getSEOLocale(),
                    platform: params.platform,
                    currency: Locale.getCurrencyCodeEN(),
                    uid: Analytics.visitorId,
                },
            });
        } catch (e) {
            reportEvent('Error during track user event', 'Analytics.trackUser', 'error', e);
        }
    };

    /**
     * Emits a trackSearch event to GTM
     * @function trackSearch
     * @param {TTrackSearchEvent} - Track search event details
     * @returns {void}
     */
    public static trackSearch = (params: TTrackSearchEvent): void => {
        try {
            if (!Analytics.visitorId) {
                Analytics.initialize();
            }

            TagManager.dataLayer({
                dataLayer: {
                    event: 'search',
                    eventAction: params.eventAction,
                    eventLabel: params.eventLabel,
                    value: params.value,
                    locale: Locale.getSEOLocale(),
                    platform: params.platform,
                    currency: Locale.getCurrencyCodeEN(),
                    uid: Analytics.visitorId,
                },
            });
        } catch (e) {
            reportEvent('Error during track search event', 'Analytics.trackSearch', 'error', e);
        }
    };

    /**
     * Emits a trackPage event to GTM
     * @function trackImpression
     * @param {TTrackImpression} - Track impression event details
     * @returns {void}
     */
    public static trackImpression = (params: TTrackImpression): void => {
        try {
            if (!Analytics.visitorId) {
                Analytics.initialize();
            }

            TagManager.dataLayer({
                dataLayer: {
                    event: 'promotionView',
                    locale: Locale.getSEOLocale(),
                    platform: params.platform,
                    currency: Locale.getCurrencyCodeEN(),
                    uid: Analytics.visitorId,
                    ecommerce: params.ecommerce,
                },
            });
        } catch (e) {
            reportEvent('Error during track impression event', 'Analytics.trackImpression', 'error', e);
        }
    };

    /**
     * Emits a trackPage event to GTM
     * @function trackClick
     * @param {TTrackClick} - Track click event details
     * @returns {void}
     */
    public static trackClick = (params: TTrackClick): void => {
        try {
            if (!Analytics.visitorId) {
                Analytics.initialize();
            }

            TagManager.dataLayer({
                dataLayer: {
                    event: 'promotionClick',
                    locale: Locale.getSEOLocale(),
                    platform: params.platform,
                    currency: Locale.getCurrencyCodeEN(),
                    uid: Analytics.visitorId,
                    ecommerce: params.ecommerce,
                },
            });
        } catch (e) {
            reportEvent('Error during track click event', 'Analytics.trackClick', 'error', e);
        }
    };

    /**
     * Formats TCart object items into the appropriate format to be sent to GTM
     * @function formatCartItems
     * @param {TCart} - Cart object
     * @returns {any} - Array of cart items
     */
    public static formatCartItems = (data: TCart): { skuConfig: string; price: number; quantity: number }[] | null => {
        if (!data || !data.items) {
            return [];
        }

        const cartItems = [] as TCartItem[];
        data.items.forEach((item: TCartItem) => {
            if (item.kind === 'bundle') {
                cartItems.push(...item.items);
            } else {
                cartItems.push(item);
            }
        });

        try {
            return cartItems.map((item: TCartItem) => ({
                skuConfig: item.skuConfig,
                price: item.unitPrice,
                quantity: item.quantity,
            }));
        } catch (e) {
            reportEvent('Error getting cart items', 'Analytics.formatCartItems', 'error', e);
        }
        return null;
    };

    /**
     * Formats TCart object items into the appropriate format to be sent to Criteo via GTM
     * @function formatCriteoCartItems
     * @param {TCart} - Cart object
     * @returns {any} - Array of cart items
     */
    public static formatCriteoCartItems = (
        data: TCart,
    ): Array<{ id: string; price: TCartItem['unitPrice']; quantity: TCartItem['quantity'] }> | undefined => {
        try {
            return data.items.map((item: TCartItem) => ({
                id: item.skuConfig,
                price: item.unitPrice,
                quantity: item.quantity,
            }));
        } catch (e) {
            reportEvent('Error getting cart items', 'Analytics.formatCriteoCartItems', 'error', e);
        }
        return undefined;
    };

    public static recommendationsApi = async (
        event: string,
        ingestParams: TIngestParams,
    ): Promise<TRecommendationsResponse> => {
        Analytics.getVisitorId();
        const result = await Product.getRecommendations(event, ingestParams);
        return result.data;
    };
}

// #TODO: Keeping for reference, replaced by util. Delete later
// /**
//  * Initializes Google Tag Manager
//  * @function initializeGTM
//  * @returns {string} - html string with GTM initialization logic
//  */
// public static initializeGTM = (marketplaceConfig: TMarketplaceConfig = MARKETPLACE_CONFIG): { __html: string } => {
//     const { visitorId } = Analytics.analytics;
//     return {
//         __html: `
//         (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
//         new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
//         j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
//         'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
//         })(window,document,'script','dataLayer','${marketplaceConfig.gtm.gtmID}');
//         dataLayer.push({
//             event: "initUrl",
//             locale: '${Locale.getISOLocale()}',
//             visitorId: '${visitorId}',
//             initUrl: window.location.href,
//             googleAnalyticsId: '${marketplaceConfig.gtm.gaID}',
//         });`,
//     };
// };

// /**
//  * Initializes Google Tag Manager without script
//  * @function initializeGTMNoScript
//  * @returns {string} - html string with GTM initialization logic
//  */
// public static initializeGTMNoScript = (
//     marketplaceConfig: TMarketplaceConfig = MARKETPLACE_CONFIG,
// ): { __html: string } => {
//     return {
//         __html: `<iframe src="https://www.googletagmanager.com/ns.html?id=${marketplaceConfig.gtm.gtmID}"
//         height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
//     };
// };
